import { DOCX_MIME_TYPE, XML_MIME_TYPES } from '@heidi/utils';
import type {
  BoundingBox,
  ExtractedField,
  FilledPdfField,
  FormField,
  FormFieldRequest,
  PageDimensions,
  PageDimensionsByPage,
  PdfFieldType,
  Rect,
  ToExtractedFieldInput,
} from '../contracts/pdf-fields';

/**
 * Detects a Word (.docx) form-template upload by MIME type or extension.
 * Docx files are converted to PDF by the backend extract-fields endpoint.
 */
export function isDocxFile(file: File): boolean {
  return file.type === DOCX_MIME_TYPE || file.name.toLowerCase().endsWith('.docx');
}

/**
 * Detects an XML form-template upload.
 *
 * The extension is checked as well as the MIME type on purpose: browsers
 * disagree about `.xml` (Chrome says `text/xml`, Safari `application/xml`) and
 * a file dragged in from some tools arrives with an empty or `text/plain`
 * type, so MIME alone would send a real XML form down the PDF parser.
 */
export function isXmlFile(file: File): boolean {
  return (
    (XML_MIME_TYPES as readonly string[]).includes(file.type) ||
    file.name.toLowerCase().endsWith('.xml')
  );
}

/**
 * A JSON form upload. Ambiguous MIME types (`text/plain`,
 * `application/octet-stream`) count only when the name ends in `.json`, so a
 * plain-text note is not sent to the JSON extract endpoint.
 */
export function isJsonFile(file: File): boolean {
  const namedJson = file.name.toLowerCase().endsWith('.json');
  if (file.type === 'application/json' || file.type === 'text/json') return true;
  if (!namedJson) return false;
  return (
    file.type === '' ||
    file.type === 'text/plain' ||
    file.type === 'application/octet-stream'
  );
}

export const PDF_FIELD_TEXT: PdfFieldType = 'Text';
export const PDF_FIELD_BUTTON: PdfFieldType = 'Button';
export const PDF_FIELD_CHECKBOX: PdfFieldType = 'CheckBox';
export const PDF_FIELD_COMBOBOX: PdfFieldType = 'ComboBox';
export const PDF_FIELD_LISTBOX: PdfFieldType = 'ListBox';
export const PDF_FIELD_RADIOBUTTON: PdfFieldType = 'RadioButton';
export const PDF_FIELD_SIGNATURE: PdfFieldType = 'Signature';
export const PDF_FIELD_DATE: PdfFieldType = 'Date';
export const PDF_FIELD_NUMBER: PdfFieldType = 'Number';

/** Selectable types in the form editor. Dropdown = ComboBox for new picks. */
export const PDF_FIELD_TYPES: PdfFieldType[] = [
  PDF_FIELD_TEXT,
  PDF_FIELD_CHECKBOX,
  PDF_FIELD_RADIOBUTTON,
  PDF_FIELD_SIGNATURE,
  PDF_FIELD_COMBOBOX,
  PDF_FIELD_DATE,
  PDF_FIELD_NUMBER,
];

export const PDF_FIELD_CHOICE_TYPES: ReadonlySet<PdfFieldType> = new Set([
  PDF_FIELD_COMBOBOX,
  PDF_FIELD_LISTBOX,
]);

/**
 * Type-picker options for one field. New Dropdown picks are ComboBox.
 * Legacy ListBox fields keep ListBox in the menu so save does not rewrite them.
 */
export function getPdfFieldTypePickerTypes(
  currentType: PdfFieldType
): readonly PdfFieldType[] {
  if (currentType !== PDF_FIELD_LISTBOX) {
    return PDF_FIELD_TYPES;
  }
  return PDF_FIELD_TYPES.map((type) =>
    type === PDF_FIELD_COMBOBOX ? PDF_FIELD_LISTBOX : type
  );
}

const CHECKBOX_OFF_VALUES = new Set(['', 'off', 'no', 'false', '0']);

/** Returns true when the array contains at least one non-empty string. */
export function hasNonEmptyChoices(choices: string[] | null | undefined): boolean {
  return Array.isArray(choices) && choices.some((v) => v.trim() !== '');
}

/** Returns true when a checkbox/radio value represents a checked/selected state. */
export function isCheckboxOn(value: string): boolean {
  return !CHECKBOX_OFF_VALUES.has(value.toLowerCase());
}

/** Normalises a raw checkbox value to 'Yes' (checked) or '' (unchecked). */
export function normalizeCheckboxValue(value: string): string {
  return isCheckboxOn(value) ? 'Yes' : '';
}

/** Standard PDF page size in points (US Letter: 8.5 x 11 inches at 72 DPI) */
export const PDF_PAGE_WIDTH = 612;
export const PDF_PAGE_HEIGHT = 792;

/** Default page dimensions used when none are provided. */
export const DEFAULT_PAGE_DIMENSIONS: PageDimensions = {
  width: PDF_PAGE_WIDTH,
  height: PDF_PAGE_HEIGHT,
};

export const DEFAULT_PAGE_DIMENSIONS_BY_PAGE: PageDimensionsByPage = {
  1: DEFAULT_PAGE_DIMENSIONS,
};

export type FormFieldsToExtractedOptions = {
  /** Applied only when the source field left `font_size` unset (`null`). */
  unsetFontSizeDefault?: number | null;
};

function resolvePageDimensionsForPage(
  dimensions: PageDimensionsByPage | undefined,
  page: number | undefined
): PageDimensions {
  if (!dimensions) return DEFAULT_PAGE_DIMENSIONS;
  const pageDimensions = dimensions[page ?? 1] ?? dimensions[1];
  return pageDimensions ?? DEFAULT_PAGE_DIMENSIONS;
}

/**
 * Converts PDF point-based rect (x0, y0, x1, y1) to percentage-based bounding box (0-100).
 * Falls back to a default position when rect is null/undefined.
 *
 * @param pageDimensions - Per-page dimension map keyed by 1-based page number.
 */
export function rectToBoundingBox(
  rect: Rect | null | undefined,
  fallbackIndex: number,
  pageDimensions: PageDimensionsByPage = DEFAULT_PAGE_DIMENSIONS_BY_PAGE,
  page?: number
): BoundingBox {
  if (rect) {
    const { width: pageWidth, height: pageHeight } = resolvePageDimensionsForPage(
      pageDimensions,
      page
    );
    return {
      x: ((rect.x0 || 0) / pageWidth) * 100,
      y: ((rect.y0 || 0) / pageHeight) * 100,
      width: (((rect.x1 || 0) - (rect.x0 || 0)) / pageWidth) * 100,
      height: (((rect.y1 || 0) - (rect.y0 || 0)) / pageHeight) * 100,
    };
  }
  return { x: 10, y: 10 + fallbackIndex * 5, width: 30, height: 3 };
}

/**
 * Converts a percentage-based bounding box (0-100) to PDF point-based rect.
 *
 * @param pageDimensions - Per-page dimension map keyed by 1-based page number.
 */
export function boundingBoxToRect(
  box: BoundingBox,
  pageDimensions: PageDimensionsByPage = DEFAULT_PAGE_DIMENSIONS_BY_PAGE,
  page?: number
): {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
} {
  const { width: pageWidth, height: pageHeight } = resolvePageDimensionsForPage(
    pageDimensions,
    page
  );
  return {
    x0: (box.x / 100) * pageWidth,
    y0: (box.y / 100) * pageHeight,
    x1: ((box.x + box.width) / 100) * pageWidth,
    y1: ((box.y + box.height) / 100) * pageHeight,
  };
}

/** Converts an editor ExtractedField (0-100 percent box) to an API FormField (PDF-point rect).
 * A non-empty prompt is always sent, since a save replaces the whole field list.
 * `includePrompt` also sends an empty prompt as `''`, which clears the stored one. */
export function extractedFieldToFormField(
  field: ExtractedField,
  pageDimensions?: PageDimensionsByPage,
  options?: { includePrompt?: boolean }
): FormFieldRequest {
  const { x0, y0, x1, y1 } = boundingBoxToRect(
    field.boundingBox,
    pageDimensions,
    field.page
  );

  const result: FormFieldRequest = {
    page: field.page,
    field_name: field.fieldName || field.id,
    field_label: field.label,
    field_type: field.type,
    field_value: field.value || null,
    font_size: field.fontSize ?? null,
    choice_values: hasNonEmptyChoices(field.options) ? field.options! : null,
    rect: { x0, y0, x1, y1 },
    exclude_from_fill: field.excludeFromFill ?? false,
    ...(field.prompt || options?.includePrompt ? { prompt: field.prompt ?? '' } : {}),
  };

  return result;
}

/**
 * Converts an array of ExtractedFields to FormField array
 *
 * @param pageDimensions - Actual page size in PDF points, or per-page dimensions.
 */
export function extractedFieldsToFormFields(
  fields: ExtractedField[],
  pageDimensions?: PageDimensionsByPage,
  options?: { includePrompt?: boolean }
): FormFieldRequest[] {
  return fields.map((field) => extractedFieldToFormField(field, pageDimensions, options));
}

/**
 * Shared builder that converts a normalised input into an ExtractedField.
 */
function toExtractedField(
  input: ToExtractedFieldInput,
  index: number,
  pageDimensions?: PageDimensionsByPage,
  options?: FormFieldsToExtractedOptions
): ExtractedField {
  const boundingBox = rectToBoundingBox(input.rect, index, pageDimensions, input.page);
  const isToggle =
    input.type === PDF_FIELD_CHECKBOX || input.type === PDF_FIELD_RADIOBUTTON;
  const value = isToggle ? normalizeCheckboxValue(input.value) : input.value;

  return {
    id: `field-${index}`,
    type: input.type,
    label: input.label,
    value,
    fontSize: input.fontSize ?? options?.unsetFontSizeDefault ?? undefined,
    boundingBox,
    page: input.page,
    confidence: input.confidence,
    fieldName: input.fieldName,
    ...(hasNonEmptyChoices(input.choiceValues) ? { options: input.choiceValues! } : {}),
    ...(input.excludeFromFill ? { excludeFromFill: true } : {}),
    ...(input.prompt ? { prompt: input.prompt } : {}),
  };
}

/**
 * Converts a FormField (from the PDF template extract API) to ExtractedField.
 *
 * @param pageDimensions - Actual page size in PDF points, or per-page dimensions.
 */
export function formFieldToExtractedField(
  field: FormField,
  index: number,
  pageDimensions?: PageDimensionsByPage,
  options?: FormFieldsToExtractedOptions
): ExtractedField {
  return toExtractedField(
    {
      rect: field.rect,
      type: (field.field_type as PdfFieldType) || PDF_FIELD_TEXT,
      label: field.field_label || field.field_name || `Field ${index + 1}`,
      value: field.field_value ?? '',
      fontSize: field.font_size ?? undefined,
      page: field.page || 1,
      confidence: 90,
      fieldName: field.field_name || undefined,
      choiceValues: field.choice_values,
      excludeFromFill: field.exclude_from_fill,
      prompt: field.prompt,
    },
    index,
    pageDimensions,
    options
  );
}

/**
 * Converts an array of FormFields to ExtractedField array
 *
 * @param pageDimensions - Actual page size in PDF points, or per-page dimensions.
 */
export function formFieldsToExtractedFields(
  fields: FormField[],
  pageDimensions?: PageDimensionsByPage,
  options?: FormFieldsToExtractedOptions
): ExtractedField[] {
  return fields.map((field, index) =>
    formFieldToExtractedField(field, index, pageDimensions, options)
  );
}

/**
 * Converts a FilledPdfField (from the PDF document fields API) to ExtractedField.
 *
 * @param pageDimensions - Actual page size in PDF points, or per-page dimensions.
 */
export function filledPdfFieldToExtractedField(
  field: FilledPdfField,
  index: number,
  pageDimensions?: PageDimensionsByPage
): ExtractedField {
  return toExtractedField(
    {
      rect: field.rect,
      type: (field.field_type as PdfFieldType) || PDF_FIELD_TEXT,
      label: field.field_label || field.field_name,
      value: field.field_value,
      fontSize: field.font_size ?? undefined,
      page: field.page,
      confidence: 95,
      fieldName: field.id,
      choiceValues: field.choice_values,
    },
    index,
    pageDimensions
  );
}

/**
 * Converts an array of FilledPdfFields to ExtractedField array.
 *
 * @param pageDimensions - Actual page size in PDF points, or per-page dimensions.
 */
export function filledPdfFieldsToExtractedFields(
  fields: FilledPdfField[],
  pageDimensions?: PageDimensionsByPage
): ExtractedField[] {
  return fields.map((field, index) =>
    filledPdfFieldToExtractedField(field, index, pageDimensions)
  );
}
